import { filter, keyBy, each, values, map, uniq, find, compact } from 'lodash'
import moment from 'moment'

import {
  GET_CUSTOMER_ORDERING_PREFERENCES_REQUESTED,
  GET_CUSTOMER_ORDERING_PREFERENCES_SUCCEEDED,
  GET_CUSTOMER_ORDERING_PREFERENCES_FAILED,
  CREATE_CUSTOMER_ORDERING_PREFERENCES_REQUESTED,
  CREATE_CUSTOMER_ORDERING_PREFERENCES_SUCCEEDED,
  CREATE_CUSTOMER_ORDERING_PREFERENCES_FAILED,
  CREATE_OPTIMISTIC_CUSTOMER_ORDERING_PREFERENCES_SUCCEEDED,
  REMOVE_CUSTOMER_ORDERING_PREFERENCE_SUCCEEDED,
  REMOVE_CUSTOMER_ORDERING_PREFERENCE_FAILED,
  REMOVE_OPTIMISTIC_CUSTOMER_ORDERING_PREFERENCE_SUCCEEDED,
} from './constants'

export const defaultState = {
  byId: {},
  pendingIds: [],
  pendingPreferences: [],
  pendingRemovalPreferences: [],
  createdCustomerId: undefined,
  removedCustomerId: undefined,
  isLoading: false,
}

export default (state = defaultState, {type, payload = []}) => {
  switch (type) {

    case GET_CUSTOMER_ORDERING_PREFERENCES_REQUESTED: {
      return {
        ...state,
        isLoading: true
      }
    }

    case GET_CUSTOMER_ORDERING_PREFERENCES_SUCCEEDED: {
      let defaultsByCustomerId = {}
      payload.forEach((preference) => {
        const customerId = preference?.customerId
        const salesEventId = preference?.salesEventId
        const standUuid = preference?.standUuid ?? 'noStand'

        if (!salesEventId && customerId) {
          if (!defaultsByCustomerId[customerId]) defaultsByCustomerId[customerId] = {}
          if (!defaultsByCustomerId[customerId][standUuid]) defaultsByCustomerId[customerId][standUuid] = []
          defaultsByCustomerId[customerId][standUuid].push(preference.uuid)
        }
      })

      const mappedPayloadIds = map(payload, preference => preference.uuid)

      const filteredPendingPreferences = filter(state.pendingPreferences, preference => !mappedPayloadIds.includes(preference.uuid) || !moment(preference.expiresAt).isAfter(moment()))

      const isOneSalesEventId = uniq(compact(map(payload, preference => preference.salesEventId))).length === 0

      const firstEventCustomerPreference = isOneSalesEventId ? find(payload, payloadItem => !!payloadItem) : undefined

      const filteredPendingRemovalPreferences = filter(state.pendingRemovalPreferences, removalPreference => {
        return mappedPayloadIds.includes(removalPreference.uuid) || !moment(removalPreference.expiresAt).isAfter(moment()) || (firstEventCustomerPreference && removalPreference.salesEventId !== firstEventCustomerPreference.salesEventId)
      })

      const preferencesById = keyBy(payload, 'uuid')

      each(filteredPendingRemovalPreferences, preference => {
        delete preferencesById[preference.uuid]
      })

      return {
        ...state,
        byId: {
          ...preferencesById,
          ...keyBy(state.pendingPreferences, 'uuid')
        },
        defaultsByCustomerId,
        pendingPreferences: filteredPendingPreferences,
        pendingRemovalPreferences: filteredPendingRemovalPreferences,
        isLoading: false,
      }
    }

    case GET_CUSTOMER_ORDERING_PREFERENCES_FAILED:
      return { ...state, isLoading: false }

    case CREATE_CUSTOMER_ORDERING_PREFERENCES_REQUESTED: {
      return {
        ...state,
        isLoading: true,
      }
    }

    case CREATE_CUSTOMER_ORDERING_PREFERENCES_SUCCEEDED: {
      return {
        byId: {
          ...state.byId,
          ...keyBy(payload, 'uuid'),
        },
        pendingPreferences: [...state.pendingPreferences],
        defaultsByCustomerId: {
          ...state.defaultsByCustomerId
        },
        pendingRemovalPreferences: [
          ...state.pendingRemovalPreferences
        ],
        createdCustomerId: payload[0]?.uuid ?? undefined,
        removedCustomerId: state.removedCustomerId,
        isLoading: false
      }
    }

    case CREATE_OPTIMISTIC_CUSTOMER_ORDERING_PREFERENCES_SUCCEEDED: {
      return {
        byId: {
          ...state.byId,
          ...keyBy(payload, 'uuid'),
        },
        defaultsByCustomerId: {
          ...state.defaultsByCustomerId
        },
        pendingPreferences: [
          ...(state.pendingPreferences || []),
          ...(map(payload, (payloadPreference) => ({ ...payloadPreference,  expiresAt: moment().add(5, 'minutes').format()}))),
        ],
        pendingRemovalPreferences: [
          ...state.pendingRemovalPreferences
        ],
        createdCustomerId: state.createdCustomerId,
        removedCustomerId: state.removedCustomerId,
        isLoading: true,
      }
    }

    case CREATE_CUSTOMER_ORDERING_PREFERENCES_FAILED:
      const payloadIds = map(payload, preference => preference.uuid)
      const filteredPendingPreferences = filter(state.pendingPreferences, pendingPreference => !payloadIds.includes(pendingPreference.uuid))
      let pendingState = { ...state.byId }

      each(payload, customer => {
        delete pendingState[customer.uuid]
      })

      return {
        byId: {
          ...pendingState,
        },
        defaultsByCustomerId: {
          ...state.defaultsByCustomerId,
        },
        pendingPreferences: [
          ...filteredPendingPreferences,
        ],
        pendingRemovalPreferences: [
          ...state.pendingRemovalPreferences,
        ],
        isLoading: false,
      }

    case REMOVE_CUSTOMER_ORDERING_PREFERENCE_SUCCEEDED: {
      const deletedPreference = payload[0]
      const byId = filter(values(state.byId), (pref) => pref.uuid !== deletedPreference.uuid)

      return {
        ...state,
        byId: {
          ...keyBy(byId, 'uuid')
        },
        removedCustomerId: deletedPreference?.uuid ?? '',
        createdCustomerId: state.createdCustomerId,
        isLoading: state.isLoading
      }
    }

    case REMOVE_CUSTOMER_ORDERING_PREFERENCE_FAILED: {
      const revertedPrefernce = filter(state.pendingRemovalPreferences, removalPreference => removalPreference.uuid === payload)
      const filteredPendingRemovalPreferences = filter(state.pendingRemovalPreferences, removalPreference => removalPreference.uuid !== payload)
      return {
        ...state,
        byId: {
          ...state.byId,
          ...keyBy(revertedPrefernce, 'uuid')
        },
        pendingRemovalPreferences: filteredPendingRemovalPreferences,
      }
    }

    case REMOVE_OPTIMISTIC_CUSTOMER_ORDERING_PREFERENCE_SUCCEEDED: {
      const removedPreference = state.byId[payload]
      const filteredPreferences = filter(values(state.byId), (pref) => pref.uuid !== payload)

      return {
        ...state,
        byId: {
          ...keyBy(filteredPreferences, 'uuid')
        },
        pendingRemovalPreferences: [...(state.pendingRemovalPreferences || []), {...removedPreference, expiresAt: moment().add(5, 'minutes').format()}],
        isLoading: true,
      }
    }

    default:
      return state
  }
}
